<template>
  <div
    class="error"
    :data-testid="dataTestId.errorScreen.block"
  >
    <div class="error-wrapper">
      <div
        class="error-cat-bg"
        :data-testid="dataTestId.errorScreen.cat"
      >
        <LottieAnimatedElement :animation />
      </div>
      <div class="error-text">
        <h3 :data-testid="dataTestId.errorScreen.title">{{ title }}</h3>
        <p
          class="body-text-s regular"
          :data-testid="dataTestId.errorScreen.description"
        >
          {{ text }}
        </p>
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import LottieAnimatedElement from "@/components/LottieAnimatedElement.vue"
import {AnimationFileNameEnum} from "@/components/types"
import {useRouter} from "vue-router"
import {computed, inject, onBeforeUnmount, onMounted, ref} from "vue"
import {useErrorStore} from "@/stores/error"
import {storeToRefs} from "pinia"
import {trackEvent} from "@/services/analytics.ts"
import {GaAnalyticsEvents} from "@/types/ga.types.ts"
import dataTestId from "@/constants/dataTestId"

const forbiddenErrorStatusCode = [400, 403]
const t = inject<(key: string) => string>("t", (k) => k)
const errorStore = useErrorStore()
const {activeError} = storeToRefs(errorStore)
const error = ref(activeError)

const title = ref(t("errorsPage.500.title"))
const text = ref(t("errorsPage.500.text"))
const animation = ref(AnimationFileNameEnum.failed)

const isAccessError = computed(() =>
  // @ts-ignore
  error.value ? forbiddenErrorStatusCode.includes(error.value.statusCode) : false
)
if (error.value) {
  title.value = isAccessError.value ? t("errorsPage.403.title") : t("errorsPage.500.title")
  text.value = isAccessError.value ? t("errorsPage.403.text") : t("errorsPage.500.text")
  animation.value = error.value.animation ?? AnimationFileNameEnum.failed
}

const router = useRouter()
const hook = router.afterEach(() => {
  errorStore.activeError = null
})

onMounted(() => {
  if (isAccessError.value) {
    trackEvent(GaAnalyticsEvents.ACCESS_DENIED_EVENT, GaAnalyticsEvents.ACCESS_DENIED_LABEL)
  }
})

onBeforeUnmount(() => {
  // This will remove the hook
  hook()
})
</script>
<style scoped lang="less">
@import "@/assets/variables.less";
.error {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
  align-items: center;
  &-wrapper {
    margin: 0 auto;
    max-width: 500px;
    padding: 24px;
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 8px;
    text-align: center;
  }
  &-cat-bg {
    width: 100%;
    height: 100%;
    background: url("@/assets/icons/cat-error-red-background.svg") no-repeat center center;
    padding: 32px 16px 12px 16px;
    background-size: auto;
    @media (min-width: @mobile-max-width) {
      padding: 40px 76px 24px 76px;
    }
  }
  &-text p {
    color: var(--fg-soft);
    margin-top: 8px;
  }
}
</style>
